/**
 * Priority strategies for mixed selections - can't show all 8 actions at once.
 *
 * Strategy priority: pending > approved > rejected > paid workflows.
 * Example: [2 pending, 3 approved] → shows only approve/reject actions.
 *
 * Pure function: ActionEligibility → AvailableActions (UI buttons or warning).
 */
import i18n from 'modules/core/lib/i18n'

import { ActionEligibility } from './types'

export const ACTION_IDS = {
  APPROVE: 'approve',
  REJECT: 'reject',
  REIMBURSE_PAYROLL: 'reimburse-payroll',
  REIMBURSE_SEPA: 'reimburse-sepa',
  SET_PAID: 'set-paid',
  RESET_APPROVAL: 'reset-approval',
  REMOVE_FROM_PAYROLL: 'remove-from-payroll',
  REVERT_PAID: 'revert-paid',
  VIEW_GROUP: 'view-group',
} as const

export type ActionId = (typeof ACTION_IDS)[keyof typeof ACTION_IDS]

export interface ActionButton {
  id: ActionId
  label: string
}

export type AvailableActions =
  | {
      primary: ActionButton[]
      secondary?: ActionButton[]
    }
  | {
      warningMessage: string
    }

type ActionStrategy = (eligibility: ActionEligibility) => AvailableActions | null

/**
 * Strategy for handling pending expenses (approve/reject flow)
 */
const pendingStrategy: ActionStrategy = (eligibility) => {
  if (!eligibility.canApprove && !eligibility.canReject) return null

  const primary: ActionButton[] = []
  const secondary: ActionButton[] = []

  if (eligibility.canApprove) {
    primary.push({
      id: ACTION_IDS.APPROVE,
      label: i18n.t('expenses_v2.list.actions.approve'),
    })
  }

  if (eligibility.canReject) {
    secondary.push({
      id: ACTION_IDS.REJECT,
      label: i18n.t('expenses_v2.list.actions.reject'),
    })
  }

  return { primary, secondary }
}

/**
 * Strategy for handling approved expenses (reimbursement flow)
 */
const approvedStrategy: ActionStrategy = (eligibility) => {
  if (
    !eligibility.canReimbursePayroll &&
    !eligibility.canReimburseSepa &&
    !eligibility.canSetPaid
  ) {
    return null
  }

  const primary: ActionButton[] = []
  const secondary: ActionButton[] = []

  const hasPayroll = eligibility.canReimbursePayroll
  const hasSepa = eligibility.canReimburseSepa
  const hasAnyReimbursement = hasPayroll || hasSepa

  if (hasAnyReimbursement) {
    // Always show "Reimburse" as primary, regardless of which method is available
    // This ensures consistent UX whether user has payroll, SEPA, or both
    const primaryActionId = hasPayroll ? ACTION_IDS.REIMBURSE_PAYROLL : ACTION_IDS.REIMBURSE_SEPA

    primary.push({
      id: primaryActionId,
      label: i18n.t('expenses_v2.list.actions.reimburse'),
    })

    // Expanded actions: Show detailed reimbursement options
    if (hasPayroll) {
      primary.push({
        id: ACTION_IDS.REIMBURSE_PAYROLL,
        label: i18n.t('expenses_v2.list.actions.reimburse_through_payroll'),
      })
    }

    if (hasSepa) {
      primary.push({
        id: ACTION_IDS.REIMBURSE_SEPA,
        label: i18n.t('expenses_v2.list.actions.reimburse_through_sepa_file'),
      })
    }
  }

  if (eligibility.canSetPaid) {
    secondary.push({
      id: ACTION_IDS.SET_PAID,
      label: i18n.t('expenses_v2.list.actions.set_as_paid'),
    })
  }

  // Also show reset approval if available (for approved items)
  if (eligibility.canResetApproval) {
    secondary.push({
      id: ACTION_IDS.RESET_APPROVAL,
      label: i18n.t('expenses_v2.list.actions.reset_approval_flow'),
    })
  }

  return { primary, secondary }
}

/**
 * Strategy for handling rejected expenses (restart flow)
 * Note: This strategy will only trigger if approvedStrategy doesn't match,
 * ensuring canResetApproval for rejected items is handled here.
 */
const rejectedStrategy: ActionStrategy = (eligibility) => {
  if (!eligibility.canResetApproval) return null

  const primary: ActionButton[] = []

  primary.push({
    id: ACTION_IDS.RESET_APPROVAL,
    label: i18n.t('expenses_v2.list.actions.reset_approval_flow'),
  })

  return { primary, secondary: [] }
}

/**
 * Strategy for handling paid/in payroll/in SEPA expenses (cleanup actions)
 */
const paidStrategy: ActionStrategy = (eligibility) => {
  if (!eligibility.canRemovePayroll && !eligibility.canRevertPaid) {
    return null
  }

  const primary: ActionButton[] = []
  const secondary: ActionButton[] = []

  if (eligibility.canRemovePayroll) {
    primary.push({
      id: ACTION_IDS.REMOVE_FROM_PAYROLL,
      label: i18n.t('expenses_v2.list.actions.remove_from_payroll'),
    })
  }

  if (eligibility.canRevertPaid) {
    secondary.push({
      id: ACTION_IDS.REVERT_PAID,
      label: i18n.t('expenses_v2.buttons.revert_paid'),
    })
  }

  return { primary, secondary }
}

/**
 * Strategies ordered by priority:
 * 1. Pending workflow (approve/reject) - triggered by canApprove/canReject
 * 2. Approved workflow (reimbursement) - triggered by reimbursement/setToPaid capabilities
 * 3. Rejected workflow (restart) - triggered by canResetApproval (if not matched by approved)
 * 4. Paid/In payroll/In SEPA (cleanup) - triggered by status + cleanup capabilities
 */
const strategies: ActionStrategy[] = [
  pendingStrategy,
  approvedStrategy,
  rejectedStrategy,
  paidStrategy,
]

/**
 * Pure function that determines which bulk actions should be available
 * based on the eligibility of selected items.
 *
 * Uses strategy pattern to evaluate each status in priority order,
 * returning the first matching strategy's actions.
 */
export const determineAvailableActions = (eligibility: ActionEligibility): AvailableActions => {
  // If all selected items are non-actionable, show warning
  if (eligibility.allNonActionable) {
    return {
      warningMessage: i18n.t('expenses_v2.list.actions.no_actions_available'),
    }
  }

  // Evaluate strategies in priority order, return first match
  for (const strategy of strategies) {
    const result = strategy(eligibility)
    if (result) return result
  }

  return { primary: [], secondary: [] }
}
